import { Component, Input, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MultiFormAccordionComponent } from '@/components/multi-form-accordion/multi-form-accordion.component';
import { MultiFormAccordionService } from '@/components/multi-form-accordion/multi-form-accordion.service';
import { MultiFormAccordionProps } from '@/components/multi-form-accordion/multiform-accordion.types';
import { CompanyService } from '@/pages/settings/company/company.service';
import { DacService } from '../dac.service';
import { MessageService } from 'primeng/api';
import { SearchableSelectOption } from '@/components/form-builder/form-builder.types';
import { ButtonModule } from 'primeng/button';
import { BehaviorSubject, of } from 'rxjs';
import { bidderStoreFactory, BidderDto, BidderStateProps } from '@/store/bidder-state/bidder-state.store';
import { BidderRepositoryFactory } from '@/store/bidder-state/bidder-state.repository';
import { ElfBaseStore } from '@/store/base-store.repository';

@Component({
  selector: 'sm-submit-bidders-dialog',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MultiFormAccordionComponent,
    ButtonModule,
  ],
  providers: [MultiFormAccordionService],
  templateUrl: './submit-bidders-dialog.component.html',
})
export class SubmitBiddersDialogComponent implements OnInit, OnDestroy {
  @Input() aoId!: number;
  @Input() onSaveSuccess!: () => void;
  @Input() onClose!: () => void;

  companyService = inject(CompanyService);
  dacService = inject(DacService);
  messageService = inject(MessageService);
  multiFormAccordionService = inject(MultiFormAccordionService);

  companies$ = new BehaviorSubject<SearchableSelectOption[]>([]);
  lotsOptions$ = new BehaviorSubject<SearchableSelectOption[]>([]);
  accordionObject?: MultiFormAccordionProps;

  ngOnInit() {
    this.companyService.getNonBlacklistedCompanies().subscribe({
      next: (list) => {
        this.companies$.next(list.map(company => ({
          name: `${company.nom}-${company.nif}-${company.address.pays.code}`,
          code: company.id,
        })));
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Erreur lors du chargement des sociétés',
        });
      }
    });

    this.dacService.getDacsByAo(this.aoId).subscribe({
      next: (res) => {
        const dac = res?.content?.[0];
        const lots = dac?.realisation?.lots || [];
        const lotsOptions = lots
          .filter((lot: any) => !lot.isDeleted)
          .map((lot: any) => ({
            name: lot.description,
            code: lot.id,
          }));
        this.lotsOptions$.next(lotsOptions);
      }
    });

    // Configure the multi form accordion service
    this.multiFormAccordionService.formFactory = (id: string) => bidderStoreFactory(id);

    // Fetch existing bidders and populate the accordion
    this.dacService.getBiddersList(this.aoId).subscribe({
      next: (res) => {
        const bidders = res?.bidders || [];
        if (bidders.length > 0) {
          this.setBiddersDtos(bidders);
        } else {
          this.multiFormAccordionService.reset(1); // initialize with 1 item
        }
      },
      error: () => {
        this.multiFormAccordionService.reset(1); // fallback to 1 item
      }
    });

    // Import the form component dynamically to avoid circular dependencies
    import('./bidder-form.component').then(({ BidderFormComponent }) => {
      this.accordionObject = {
        titles: ['Soumissionnaire'],
        component: BidderFormComponent,
        injectKeys: ['companies', 'isDisabled', 'lotsOptions'],
        companies: this.companies$,
        isDisabled: of(false),
        lotsOptions: this.lotsOptions$,
        customFactoryMultiFormAccordion: this.multiFormAccordionService,
      };
    });
  }

  setBiddersDtos(bidders: BidderDto[]): void {
    this.multiFormAccordionService.reset(0);

    bidders.forEach((bidder, index) => {
      const storeId = index.toString();
      const newStore = this.multiFormAccordionService._formFactory?.(storeId);
      if (!newStore) return;

      const root = this.multiFormAccordionService.root;
      if ('children' in root) {
        root.children.push({ store: newStore });
      }

      this.multiFormAccordionService.newStore$.next(newStore);

      const repo = BidderRepositoryFactory.create(newStore);
      repo.set('dto', bidder);
    });
  }

  submit() {
    const stores = this.multiFormAccordionService.getAllStores();
    const biddersData = stores.map((store: ElfBaseStore<BidderStateProps>) => {
      const repo = BidderRepositoryFactory.create(store);
      return repo.get<BidderDto>('dto');
    });

    // Validate each bidder
    const hasLots = this.lotsOptions$.value.length > 0;
    for (let i = 0; i < biddersData.length; i++) {
      const bidder = biddersData[i];

      if (hasLots) {
        if (!bidder.lotIds || bidder.lotIds.length === 0) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Validation',
            detail: `Veuillez sélectionner au moins un lot pour le soumissionnaire ${i + 1}`,
          });
          MultiFormAccordionService.openPanelIfClosed(`mfa-${i}`);
          return;
        }
        if (bidder.lotIds.length > 3) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Validation',
            detail: `Le soumissionnaire ${i + 1} ne peut pas sélectionner plus de 3 lots`,
          });
          MultiFormAccordionService.openPanelIfClosed(`mfa-${i}`);
          return;
        }
      }

      if (bidder.typeSoumission === 'INDIVIDUEL') {
        if (!bidder.societeId) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Validation',
            detail: `Veuillez sélectionner une société pour le soumissionnaire ${i + 1}`,
          });
          MultiFormAccordionService.openPanelIfClosed(`mfa-${i}`);
          return;
        }
      } else {
        if (!bidder.groupSoumissionList || bidder.groupSoumissionList.length === 0) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Validation',
            detail: `Veuillez ajouter au moins un membre pour le groupement ${i + 1}`,
          });
          MultiFormAccordionService.openPanelIfClosed(`mfa-${i}`);
          return;
        }
        let hasChef = false;
        for (let j = 0; j < bidder.groupSoumissionList.length; j++) {
          const member = bidder.groupSoumissionList[j];
          if (!member.societeId) {
            this.messageService.add({
              severity: 'warn',
              summary: 'Validation',
              detail: `Veuillez sélectionner une société pour le membre ${j + 1} du groupement ${i + 1}`,
            });
            MultiFormAccordionService.openPanelIfClosed(`mfa-${i}`);
            return;
          }
          if (member.chefDeFile) {
            hasChef = true;
          }
        }
        if (!hasChef) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Validation',
            detail: `Veuillez définir un chef de file pour le groupement ${i + 1}`,
          });
          MultiFormAccordionService.openPanelIfClosed(`mfa-${i}`);
          return;
        }
      }
    }

    const payload = {
      bidders: biddersData.map(b => ({
        soumissionId: b.soumissionId,
        groupSoumissionId: b.groupSoumissionId,
        societeId: b.typeSoumission === 'INDIVIDUEL' ? b.societeId : null,
        typeSoumission: b.typeSoumission,
        status: b.status,
        lotIds: b.lotIds,
        groupSoumissionList: b.typeSoumission === 'GROUPEMENT' ? b.groupSoumissionList : null
      }))
    };

    this.dacService.submitBiddersList(this.aoId, payload).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: 'Liste des soumissionnaires soumise avec succès',
        });
        this.onSaveSuccess();
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error?.message || 'Erreur lors de la soumission de la liste',
        });
      }
    });
  }

  cancel() {
    this.onClose();
  }

  ngOnDestroy() {
    this.multiFormAccordionService.reset(0);
  }
}
